import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TranslationEditor = () => {
  const [translations, setTranslations] = useState({});
  const [selectedFile, setSelectedFile] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [editedTranslations, setEditedTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  const serverBaseUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:5000';
  const languages = ['en', 'pl', 'ukr', 'ru'];

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const response = await axios.get(`${serverBaseUrl}/api/translations`);
        setTranslations(response.data);
        const firstFile = Object.keys(response.data)[0];
        setSelectedFile(firstFile);
        setEditedTranslations(response.data[firstFile]);
      } catch (error) {
        console.error('Error fetching translations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTranslations();
  }, [serverBaseUrl]);

  useEffect(() => {
    if (selectedFile && translations[selectedFile]) {
      setEditedTranslations({ ...translations[selectedFile] });
    }
  }, [selectedFile, translations]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.value);
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
  };

  const handleTextChange = (path, value) => {
    setEditedTranslations((prev) => {
      const updated = { ...prev };
      let current = updated;

      const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = {
        ...current[keys[keys.length - 1]],
        [selectedLanguage]: value,
      };

      return updated;
    });
  };

  const handleSave = async () => {
    try {
      await axios.post(`${serverBaseUrl}/api/translations/${selectedFile}`, editedTranslations);
      setTranslations((prev) => ({
        ...prev,
        [selectedFile]: { ...editedTranslations },
      }));
      alert(`Changes to ${selectedFile} saved successfully!`);
    } catch (error) {
      console.error('Error saving translations:', error);
      alert('Failed to save changes');
    }
  };

  const getTextFields = (obj, parentPath = '') => {
    const fields = [];
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      if (typeof value === 'object' && value !== null) {
        if (languages.includes(Object.keys(value)[0])) {
          fields.push({ path: currentPath, text: value[selectedLanguage] || '' });
        } else {
          fields.push(...getTextFields(value, currentPath));
        }
      }
    }
    return fields;
  };

  if (loading) return <div>Loading translations...</div>;

  const textFields = selectedFile ? getTextFields(editedTranslations) : [];

  return (
    <div className="translation-editor">
      <h1>Translation Editor</h1>
      <div className="controls">
        <div className="select-container">
          <label htmlFor="file-select">Select File:</label>
          <select id="file-select" value={selectedFile} onChange={handleFileChange}>
            {Object.keys(translations).map((file) => (
              <option key={file} value={file}>
                {file}
              </option>
            ))}
          </select>
        </div>
        <div className="select-container">
          <label htmlFor="language-select">Select Language:</label>
          <select id="language-select" value={selectedLanguage} onChange={handleLanguageChange}>
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        <button className="save-button" onClick={handleSave}>
          Save Changes
        </button>
      </div>
      <div className="text-fields">
        {textFields.map((field) => (
          <div key={field.path} className="text-field">
            <label>{field.path}</label>
            <textarea
              value={field.text}
              onChange={(e) => handleTextChange(field.path, e.target.value)}
              rows={Math.max(field.text.split('\n').length, 3)}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TranslationEditor;